import type { Middleware } from "openapi-fetch";

/**
 * Reads `X-Correlation-Id` from every response and:
 *   - Tags the current Sentry scope (if Sentry is initialized — apps init in main.tsx)
 *   - Attaches `linkhr.correlation_id` to the current OTel active span
 *
 * Apps expose the SDK instances via two well-known globals set at bootstrap:
 *   - `globalThis.__linkhrSentry__`  — the Sentry namespace (e.g. `@sentry/react`'s `*` import)
 *   - `globalThis.__linkhrOtel__`    — `{ trace }` from `@opentelemetry/api`
 *
 * Keeping Sentry + OTel as optional globals means `@linkhr/api-client` has no
 * hard runtime dependency on either. Apps that don't install them still get
 * a working client; the correlation middleware is a no-op for them.
 *
 * Header casing: backend emits `X-Correlation-Id` but HTTP headers are
 * case-insensitive — we check both common casings defensively.
 */
type MaybeSentry =
  | { getCurrentScope?: () => { setTag?: (k: string, v: string) => void } }
  | undefined;
type MaybeOtel =
  | {
      trace?: {
        getActiveSpan?: () => { setAttribute?: (k: string, v: string) => void } | undefined;
      };
    }
  | undefined;

declare global {
  // Sentry + OTel are optional runtime deps — apps set these at bootstrap if installed.
  var __linkhrSentry__: MaybeSentry;
  var __linkhrOtel__: MaybeOtel;
}

export const correlationMiddleware: Middleware = {
  onResponse({ response }) {
    const cid =
      response.headers.get("X-Correlation-Id") ?? response.headers.get("x-correlation-id");
    if (!cid) return undefined;

    // Sentry tag — lazy, no hard dep
    try {
      const Sentry = globalThis.__linkhrSentry__;
      Sentry?.getCurrentScope?.().setTag?.("correlation_id", cid);
    } catch {
      /* no-op — Sentry not initialized or threw */
    }

    // OTel active span attribute — lazy, no hard dep
    try {
      const otel = globalThis.__linkhrOtel__;
      const span = otel?.trace?.getActiveSpan?.();
      span?.setAttribute?.("linkhr.correlation_id", cid);
    } catch {
      /* no-op — OTel not initialized or threw */
    }

    return undefined;
  },
};
